if (!customElements.get('product-video-modal')) {
  customElements.define(
    'product-video-modal',
    class ProductVideoModal extends HTMLElement {
      constructor() {
        super();
        this.drawerObserver = null;
        this.resizeObserver = null;
        this.activeIndex = null;
        this.handleClick = this.handleClick.bind(this);
        this.handleResize = this.handleResize.bind(this);
      }

      connectedCallback() {
        this.addEventListener('click', this.handleClick);
        window.addEventListener('resize', this.handleResize);
        this.observeDrawer();

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(this.handleResize);
          this.querySelectorAll('.cyril-video-picker__player-item').forEach((item) => {
            this.resizeObserver.observe(item);
          });
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        window.removeEventListener('resize', this.handleResize);
        this.drawerObserver?.disconnect();
        this.resizeObserver?.disconnect();
      }

      observeDrawer() {
        const drawer = this.closest('side-drawer');
        if (!drawer) return;

        this.drawerObserver = new MutationObserver(() => {
          const isOpen = drawer.classList.contains('open') || drawer.classList.contains('active');
          if (!isOpen) this.reset();
        });

        this.drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
      }

      refresh() {
        this.reset();
      }

      reset() {
        this.pauseAllMedia();
        this.activeIndex = null;
        this.classList.remove('is-player');
        this.closest('.cyril-video-picker__content')?.classList.remove('is-player');

        const selectView = this.querySelector('[data-video-select-view]');
        const playerView = this.querySelector('[data-video-player-view]');

        selectView?.classList.remove('hidden');
        playerView?.classList.add('hidden');
        playerView?.setAttribute('aria-hidden', 'true');

        this.getPlayerItems().forEach((item) => {
          item.classList.add('hidden');
          item.setAttribute('aria-hidden', 'true');
        });
      }

      handleClick(event) {
        const openButton = event.target.closest('[data-video-open]');
        if (openButton && this.contains(openButton)) {
          event.preventDefault();
          const index = this.getVisibleCards().indexOf(openButton);
          if (index >= 0) this.openPlayer(index);
          return;
        }

        const backButton = event.target.closest('[data-video-back]');
        if (backButton && this.contains(backButton)) {
          event.preventDefault();
          this.reset();
        }
      }

      openPlayer(index) {
        const items = this.getVisiblePlayerItems();
        const item = items[index];
        if (!item) return;

        this.pauseAllMedia();
        this.activeIndex = index;
        this.classList.add('is-player');
        this.closest('.cyril-video-picker__content')?.classList.add('is-player');

        const selectView = this.querySelector('[data-video-select-view]');
        const playerView = this.querySelector('[data-video-player-view]');

        selectView?.classList.add('hidden');
        playerView?.classList.remove('hidden');
        playerView?.setAttribute('aria-hidden', 'false');

        this.getPlayerItems().forEach((playerItem) => {
          const active = playerItem === item;
          playerItem.classList.toggle('hidden', !active);
          playerItem.setAttribute('aria-hidden', active ? 'false' : 'true');
        });

        window.setTimeout(() => {
          this.updateFrameSize(item);
          this.playItem(item);
        }, 80);
      }

      getVisibleCards() {
        return Array.from(this.querySelectorAll('.cyril-video-picker__card:not(.hidden)'));
      }

      getPlayerItems() {
        return Array.from(this.querySelectorAll('[data-video-player-item]'));
      }

      getVisiblePlayerItems() {
        return this.getPlayerItems().filter((item) => !item.classList.contains('hidden-by-filter'));
      }

      handleResize() {
        const item = this.querySelector('[data-video-player-item]:not(.hidden)');
        if (item) this.updateFrameSize(item);
      }

      updateFrameSize(item) {
        const frame = item.querySelector('.cyril-video-picker__frame');
        if (!frame) return;

        const rect = item.getBoundingClientRect();
        if (!rect.width || !rect.height) return;

        const styles = window.getComputedStyle(item);
        const paddingX = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
        const paddingY = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
        const availableWidth = Math.max(1, rect.width - paddingX);
        const availableHeight = Math.max(1, rect.height - paddingY);
        const ratio = this.getItemRatio(item);

        let fitWidth = availableWidth;
        let fitHeight = availableWidth / ratio;

        if (fitHeight > availableHeight) {
          fitHeight = availableHeight;
          fitWidth = availableHeight * ratio;
        }

        frame.style.setProperty('--video-fit-width', `${Math.floor(fitWidth)}px`);
        frame.style.setProperty('--video-fit-height', `${Math.floor(fitHeight)}px`);
      }

      getItemRatio(item) {
        const ratio = parseFloat(item.dataset.videoRatio || '1.777');
        return Number.isFinite(ratio) && ratio > 0 ? ratio : 1.777;
      }

      playItem(item) {
        const media = item.querySelector('deferred-media');
        if (!media) return;

        if (typeof media.loadContent === 'function') {
          media.loadContent(false);
        }

        window.setTimeout(() => {
          if (typeof media.playMedia === 'function') media.playMedia();
        }, 80);
      }

      pauseAllMedia() {
        this.pauseMediaInside(this);
      }

      pauseMediaInside(root) {
        root.querySelectorAll('deferred-media').forEach((media) => {
          if (typeof media.pauseMedia === 'function') media.pauseMedia();
        });

        root.querySelectorAll('video').forEach((video) => {
          try {
            video.pause();
          } catch (error) {
            // Ignore media state errors.
          }
        });

        root.querySelectorAll('iframe[data-video-type]').forEach((iframe) => {
          const type = iframe.dataset.videoType;
          const message = type === 'youtube'
            ? '{"event":"command","func":"pauseVideo","args":""}'
            : '{"method":"pause"}';
          iframe.contentWindow?.postMessage(message, '*');
        });
      }
    }
  );
}
